import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { DialogContent, Divider, List, ListItem, ListItemText } from '@mui/material'
import HHDialogActions from 'components/common/HHDialogActions'
import T from 'T'
import { get, groupBy, noop, sortBy } from 'lodash'
import SaveButton from 'components/buttons/SaveButton'
import CancelButton from 'components/buttons/CancelButton'
import Dialog from '@mui/material/Dialog'
import AddLateFeeItem from 'components/customer-details/content/fees/late_fees/AddLateFeesDialog/AddLateFeeItem'
import { useLazyFetchAccountLateFeesQuery, useLazyFetchLateFeesQuery, useLinkLateFeesToAccountMutation } from 'api/pricing/lateFeesCrud'
import AddLateFeeItemSkeleton from 'components/customer-details/content/fees/late_fees/AddLateFeesDialog/AddLateFeeItemSkeleton'
import { handleError } from 'utils/error'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'

const groupLateFees = (accountLateFees, lateFees) => {
  const checkedSet = new Set(accountLateFees.map(fee => fee.id))

  const sortedLateFees = sortBy(
    lateFees.filter(({ archived }) => !archived),
    ({ feeName = '' }) => feeName.toLowerCase()
  )

  return groupBy(sortedLateFees, fee => (checkedSet.has(fee.id) ? 'checked' : 'unchecked'))
}

const AddLateFeesDialog = ({ open = false, onClose = noop, accountId }) => {
  const listSx = {
    maxHeight: {
      lg: 64 * 5,
      md: 64 * 3,
      sm: 64 * 3,
      xs: 64 * 3,
    },
    overflow: 'auto',
  }
  const [
    fetchAccountLateFees,
    {
      isLoading: isLoadingAccountLateFees,
      data: accountLateFeesData,
      isFetching: isFetchingAccountLateFees,
      isSuccess: isSuccessAccountLateFees,
    },
  ] = useLazyFetchAccountLateFeesQuery()

  const [
    fetchLateFees,
    { data: lateFeesData, isLoading: isLoadingLateFees, isFetching: isFetchingLateFees, isSuccess: isSuccessLateFees },
  ] = useLazyFetchLateFeesQuery()
  const [linkLateFeesToAccount, { isLoading }] = useLinkLateFeesToAccountMutation()
  const lateFees = useMemo(() => get(lateFeesData, 'lateFees', []), [lateFeesData])
  const [selectedLateFeesIds, setSelectedLateFeesIds] = useState([])
  const loading = useMemo(
    () => isLoadingAccountLateFees || isFetchingAccountLateFees || isLoadingLateFees || isFetchingLateFees,
    [isLoadingAccountLateFees, isFetchingAccountLateFees, isLoadingLateFees, isFetchingLateFees]
  )
  const accountLateFees = useMemo(() => get(accountLateFeesData, 'lateFees', []), [accountLateFeesData])
  const shimmersArray = new Array(10).fill()
  const { checked = [], unchecked = [] } = useMemo(() => groupLateFees(accountLateFees, lateFees), [accountLateFees, lateFees])
  const hasUncheckedLateFees = useMemo(() => unchecked?.length !== 0, [unchecked])
  const hasCheckedLateFees = useMemo(() => checked?.length !== 0, [checked])
  const fetchSelectedUnselectedFees = async () => {
    await fetchLateFees().unwrap().catch(handleError)
    await fetchAccountLateFees({ accountId }).unwrap().catch(handleError)
  }

  const handleSave = async () => {
    linkLateFeesToAccount({
      accountId,
      lateFees: selectedLateFeesIds,
    })
      .unwrap()
      .then(() => {
        setSelectedLateFeesIds([])
        onClose()
      })
      .catch(handleError)
  }

  useEffect(() => {
    if (open) {
      fetchSelectedUnselectedFees()
    }
  }, [open])

  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={onClose}>
      <HHDialogTitle title={T.ADD_LATE_FEE} onClose={onClose} />
      <DialogContent>
        <ListItem
          sx={{
            px: 1,
          }}
        >
          <ListItemText
            primary="Already added"
            slotProps={{
              primary: {
                variant: 'h6',
                color: 'textPrimary',
              },
            }}
          />
        </ListItem>
        <List disablePadding sx={listSx}>
          {isSuccessAccountLateFees && !hasCheckedLateFees && <HHSectionPlaceholder title={T.NO_LATE_FEES_LINKED_TO_ACCOUNT} />}
          {isSuccessAccountLateFees &&
            checked?.map(lateFee => {
              const { id } = lateFee
              return <AddLateFeeItem disabled indeterminate lateFee={lateFee} key={`selected-latefee${id}`} />
            })}
          {/* eslint-disable-next-line react/no-array-index-key */}
          {loading && shimmersArray.map((_, id) => <AddLateFeeItemSkeleton key={`skeleton-latefee${id}`} />)}
        </List>
        <Divider
          sx={{
            my: 2,
          }}
        />
        <List disablePadding sx={listSx}>
          {/* eslint-disable-next-line react/no-array-index-key */}
          {isSuccessLateFees && !hasUncheckedLateFees && <HHSectionPlaceholder title={T.NO_ACTIVE_LATE_FEES_TO_LINK} />}
          {loading && shimmersArray.map((_, id) => <AddLateFeeItemSkeleton key={`skeleton-latefee${id}`} />)}{' '}
          {isSuccessLateFees &&
            unchecked?.map(lateFee => {
              const { id } = lateFee
              const checked = selectedLateFeesIds.includes(id)
              const toggleLateFeeSelection = checked => {
                if (checked) {
                  setSelectedLateFeesIds([...selectedLateFeesIds, id])
                } else {
                  setSelectedLateFeesIds(selectedLateFeesIds.filter(lateFeeId => lateFeeId !== id))
                }
              }
              return <AddLateFeeItem key={`to-select-latefee${id}`} checked={checked} lateFee={lateFee} onChange={toggleLateFeeSelection} />
            })}
        </List>
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton disabled={selectedLateFeesIds.length === 0} label={T.ADD} onClick={handleSave} loading={isLoading} />
      </HHDialogActions>
    </Dialog>
  )
}

AddLateFeesDialog.propTypes = {
  open: PropTypes.bool,
  accountId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
}

export default AddLateFeesDialog
